// Stage-02 "זיהוי הנכס" (property identification) data layer. Reuses the existing
// Urbanix planning stack — GovMap parcel lookup (data/locate) + the one-shot
// `rights-autofill` endpoint (plans + permits + as-built + parcel facts) — and
// shapes the result into the spec's §8 model: PropertyIdentification, a list of
// SourceRecords, MissingSources, ApplicablePlans, and BuildingFileCategories.
//
// Identification ONLY: no rights calculation, no plan interpretation, no area
// extraction beyond what the sources already state. Those are later stages.
import apiConfig from '../../../config/api';
import { searchAddress, parcelByGushHelka, pointToItm } from './locate';

const BASE = apiConfig.apiBaseUrl || '';

function todayISO() {
  return new Date().toISOString();
}

// Compose the human address from the saved property fields.
function addressText(prop) {
  const street = [prop.street, prop.house_number].filter(Boolean).join(' ').trim();
  return [street, prop.city].filter(Boolean).join(', ').trim();
}

// A stable fingerprint of the property inputs that drive identification. When it
// matches a saved snapshot's signature, nothing relevant changed and we can
// reuse the cached snapshot instead of re-deriving + re-saving (avoids the
// re-run delay when navigating next/previous).
// Bump SIG_VERSION whenever the derivation logic changes, so existing cached
// snapshots are invalidated and re-derived (e.g. v2: containment-based parcel
// selection instead of GovMap's first ownership row).
const SIG_VERSION = 'v12';
export function propertySignature(prop) {
  const p = prop || {};
  return [SIG_VERSION, p.loc_mode, p.block, p.parcel, p.sub_parcel, p.street, p.house_number, p.city]
    .map((v) => String(v ?? '').trim()).join('|');
}

// Reverse-resolve gush/חלקה from an ITM point via GovMap's ownership layer. The
// FIRST ownership row is the address's own parcel (verified against the Tel Aviv
// building file: חולדה 8 → 6954/217). NB: the geocoded address point can land in
// a NEIGHBOUR's polygon, so point-in-polygon on it is unreliable — trust the
// ordering here, not geometry.
async function reverseGushHelka(x, y) {
  try {
    // No credentials: these public proxies answer with Allow-Origin:* , which
    // the browser rejects for credentialed requests.
    const res = await fetch(`${BASE}/api/govmap-layers?x=${Math.round(x)}&y=${Math.round(y)}`);
    if (!res.ok) return null;
    const data = await res.json();
    const o = (data.ownership || [])[0];
    const gush = o && o['גוש'] != null ? String(o['גוש']) : null;
    const helka = o && o['חלקה'] != null ? String(o['חלקה']) : null;
    return gush && helka ? { gush, helka } : null;
  } catch { return null; }
}

// Resolve the parcel to ITM coordinates (+ polygon for the map). Prefer the
// gush/חלקה centroid (authoritative + gives the outline). When only an address
// is on file, geocode it, reverse-resolve gush/חלקה from that point, and then
// fetch the parcel outline — so the map always shows the bounding polygon and
// the identification box fills gush/חלקה even for address-only projects.
// Returns { coords:{x,y}, geometry, source, gush, helka } or null.
async function resolveParcel(prop) {
  const gush = String(prop.block || '').trim();
  const helka = String(prop.parcel || '').trim();
  if (gush && helka) {
    try {
      const r = await parcelByGushHelka(gush, helka);
      if (r?.center) return { coords: { x: r.center.x, y: r.center.y }, geometry: r.geometry, source: 'GovMap · לפי גוש/חלקה', gush, helka };
    } catch { /* fall through to address */ }
  }
  const addr = addressText(prop);
  if (addr) {
    try {
      const rows = await searchAddress(addr);
      const itm = pointToItm(rows[0]?.coord);
      if (itm) {
        const rev = await reverseGushHelka(itm.x, itm.y);
        if (rev) {
          // Centre the map on the address's own parcel polygon.
          try {
            const r = await parcelByGushHelka(rev.gush, rev.helka);
            if (r?.center) return { coords: { x: r.center.x, y: r.center.y }, geometry: r.geometry, source: 'GovMap · לפי כתובת', gush: rev.gush, helka: rev.helka };
          } catch { /* keep the address point without an outline */ }
          return { coords: itm, geometry: null, source: 'GovMap · לפי כתובת', gush: rev.gush, helka: rev.helka };
        }
        return { coords: itm, geometry: null, source: 'GovMap · לפי כתובת', gush: null, helka: null };
      }
    } catch { /* no coords */ }
  }
  return null;
}

// Map a raw plan status to a clean Hebrew label + tone for the status pill.
// The plans DB stores statuses in English ("approved"/"rejected"); older rows
// may carry Hebrew text — handle both.
export function planStatusLabel(s) {
  const t = String(s || '').trim();
  const low = t.toLowerCase();
  if (low === 'approved') return { label: 'מאושרת', tone: 'ok' };
  // The plans DB lumps "מבוטלת" (cancelled/revoked) into a bucket it labels
  // "rejected" — in this dataset every "rejected" row is actually cancelled, so
  // surface the accurate word: בוטלה (e.g. תמ"א 38, which was revoked).
  if (low === 'rejected') return { label: 'בוטלה', tone: 'neutral' };
  if (low === 'cancelled' || low === 'canceled' || low === 'expired') return { label: 'בוטלה', tone: 'neutral' };
  if (low === 'in preparation' || low === 'deposited' || low === 'pending') return { label: 'בהכנה', tone: 'warn' };
  if (/תוקף|מאושר/.test(t)) return { label: 'בתוקף', tone: 'ok' };
  if (/הכנה|הפקד|מופקד|דיון/.test(t)) return { label: 'בהכנה', tone: 'warn' };
  if (/נדח/.test(t)) return { label: 'נדחתה', tone: 'neutral' };
  if (/בטל|פג/.test(t)) return { label: 'לא בתוקף', tone: 'neutral' };
  if (!t) return { label: 'לא ידוע', tone: 'neutral' };
  return { label: t, tone: 'neutral' };
}

// Map a raw plan (from rights-autofill / applicable-plans) into the internal
// shape used by the identification snapshot + the statutory stage.
export function mapPlan(p) {
  return {
    id: p.plan_ref,
    number: p.plan_ref,
    name: p.title || '—',
    type: planType(p),
    status: planStatusLabel(p.status),
    date: p.approval_date || null,
    depositDate: p.submission_date || null, // תאריך הפקדה
    onlineNumber: p.online_number || null, // מספר מקוון (מנהל התכנון)
    planUrl: p.plan_url || null,
    hasRules: (p.kb_coverage?.rules_count || 0) > 0,
    // Extracted zoning rules (KB) — so the statutory stage can populate building
    // lines / uses / rights from structured data (chunk 03 B).
    zones: (p.zones || []).map((z) => ({
      zone_code: z.zone_code, zone_name: z.zone_name,
      primary_use: z.primary_use, secondary_uses: z.secondary_uses,
      max_far_pct: z.max_far_pct, max_height_m: z.max_height_m, max_stories: z.max_stories,
      setback_front_m: z.setback_front_m, setback_side_m: z.setback_side_m, setback_rear_m: z.setback_rear_m,
      min_lot_area_m2: z.min_lot_area_m2,
      special_clauses: z.special_clauses, national_plan_refs: z.national_plan_refs,
      extraction_confidence: z.extraction_confidence, source_pdf_url: z.source_pdf_url,
    })),
  };
}

// Best-effort plan-type label from the plan ref / title. Deliberately
// conservative — leaves '—' rather than guess when there's no clear signal.
function planType(plan) {
  const hay = `${plan.plan_ref || ''} ${plan.title || ''}`;
  if (/תמ["״']?א|תמא/.test(hay)) return 'תמ״א';
  if (/שימור/.test(hay)) return 'תכנית שימור';
  if (/מתאר/.test(hay)) return 'תכנית מתאר';
  if (/רובע/.test(hay)) return 'תכנית רובע';
  if (/^תא\//.test(String(plan.plan_ref || ''))) return 'תכנית בניין עיר';
  return '—';
}

// Empty building-file categories (spec §7.2) shown until the handasa scrape
// returns. The scrape (fetchBuildingFile) replaces these with real counts+docs.
function emptyBuildingFileCategories() {
  return [
    { key: 'permits', label: 'היתרים', count: 0, docs: [] },
    { key: 'drawings', label: 'תשריטים ותוכניות', count: 0, docs: [] },
    { key: 'decisions', label: 'החלטות ועדה', count: 0, docs: [] },
    { key: 'appeals', label: 'ערעורים', count: 0, docs: [] },
    { key: 'protocols', label: 'פרוטוקולים', count: 0, docs: [] },
    { key: 'correspondence', label: 'התכתבויות', count: 0, docs: [] },
    { key: 'other', label: 'אחר', count: 0, docs: [] },
  ];
}

// Scrape the parcel's building file (תיק בניין) from handasa, grouped into
// categories. Slow (~10-30s Puppeteer) — called separately from the main pass.
// Hard timeout so the UI can't get stuck "scanning" if the request hangs.
export async function fetchBuildingFile(gush, helka) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 75_000);
  try {
    const res = await fetch(`${BASE}/api/building-archive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gush, helka }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`building file fetch failed: ${res.status}`);
    return await res.json(); // { ok, docCount, categories, fetchedAt }
  } finally {
    clearTimeout(timer);
  }
}

// Standard completions the user can upload (spec §6.2). Gaps are SOURCES the
// system can't reach on its own (graphic-log §2.3) — never individual
// documents: טופס 4 is a tik-binyan dataset, so an inaccessible building file
// surfaces as "לא ניתן לגשת לתיק הבניין" (derived live in the screen from the
// scrape state), not as a free-standing document gap. The three below are
// inherently-manual sources (spec §10) and carry the SAME names as their rows
// in the sources registry (§2.5 — one name per source, everywhere).
function deriveMissing() {
  const out = [];
  out.push({ key: 'tabu', name: 'טאבו / נסח מקרקעין', futureUse: 'אימות בעלויות, תתי-חלקות, הערות וזיקות.', importance: 'medium' });
  out.push({ key: 'survey', name: 'מדידה / תכנית מצב קיים', futureUse: 'אימות גבולות, שטח, בנוי וטופוגרפיה.', importance: 'medium' });
  out.push({ key: 'photos', name: 'תיעוד חזותי / Street View', futureUse: 'זיהוי חזותי של קומות, חזיתות ושימושים.', importance: 'low' });
  // NB: תיק מידע is NOT a general gap — it has its own box (info-file/building-file),
  // where its status/pull result is shown.
  return out.map((m) => ({ id: `miss-${m.key}`, ...m }));
}

// Build the Dataset Registry (spec v4 §2 stage-2 / §18.1): each row is a
// DATABASE the agent queried, carrying the specific DATASETS pulled from it —
// "אין להסתפק בסטטוס כללי של נאסף; צריך לדעת אילו שכבות מידע נמשכו מכל מאגר".
// Expected-but-manual databases (טאבו, מדידה, תיעוד חזותי) are listed as
// awaiting rows so stage-3 "מקור חסר" gaps have a collection target.
function deriveSources(parcel, autofill, address) {
  const date = todayISO();
  const plans = autofill?.plans || [];
  const hasParcel = Boolean(parcel?.gush && parcel?.helka);
  const rows = [
    {
      databaseId: 'db_govmap',
      source: parcel ? (parcel.source.includes('כתובת') ? 'GovMap · איתור כתובת' : 'GIS עירוני · גוש/חלקה') : 'GovMap',
      type: 'מקור עירוני',
      status: parcel ? 'collected' : 'failed',
      date,
      link: parcel ? `https://www.govmap.gov.il/?c=${Math.round(parcel.coords.x)},${Math.round(parcel.coords.y)}&z=10` : null,
      datasets: [
        { id: 'ds_govmap_parcel', name: 'זיהוי חלקה', status: parcel ? 'collected' : 'failed', link: parcel ? `https://www.govmap.gov.il/?c=${Math.round(parcel.coords.x)},${Math.round(parcel.coords.y)}&z=10` : null },
        { id: 'ds_govmap_geometry', name: 'גאומטריית חלקה', status: parcel?.geometry ? 'collected' : 'empty', link: parcel ? `https://www.govmap.gov.il/?c=${Math.round(parcel.coords.x)},${Math.round(parcel.coords.y)}&z=10` : null },
      ],
    },
    {
      // The TA GIS constraint layers are pulled automatically when the statutory
      // analysis opens (parcel-landuse identify) — listed here so the registry
      // names them even before that stage runs.
      databaseId: 'db_gis_tel_aviv',
      source: 'GIS עירוני תל אביב — שכבות ייעוד ומגבלות',
      type: 'מקור עירוני',
      status: hasParcel ? 'planned' : 'empty',
      date,
      link: null,
      note: hasParcel ? 'נאסף אוטומטית בפתיחת שלב הניתוח הסטטוטורי' : 'לא זוהתה חלקה',
      datasets: [
        { id: 'ds_gis_landuse_837', name: 'ייעודי קרקע (837)' },
        { id: 'ds_gis_conservation', name: 'שימור (682/687)' },
        { id: 'ds_gis_trees', name: 'עצים (628/574)' },
        { id: 'ds_gis_easements', name: 'זיקות הנאה (749/750)' },
        { id: 'ds_gis_noise', name: 'מפלסי רעש (786)' },
      ],
    },
    {
      databaseId: 'db_xplan',
      source: 'מאגר תכניות עירוני (XPlan)',
      type: 'מקור סטטוטורי',
      status: plans.length ? 'collected' : 'empty',
      date,
      // No single aggregate link — each of the plans has its own official
      // document link in the "תכניות חלות על הנכס" box below.
      link: null,
      note: plans.length ? `${plans.length} תכניות · ראו «תכניות חלות»` : 'לא נמצאו תכניות',
      datasets: [
        { id: 'ds_takanon', name: `תקנוני תכניות (${plans.length})` },
        { id: 'ds_tashrit', name: 'תשריטים (בקישורי התכניות)' },
      ],
    },
    {
      // דף זכויות — Tel Aviv's official מידע מוקדם report for the parcel: a
      // Database with internal section Datasets (spec §6). The link opens the
      // real rights PDF (opt=2 = existing building) and is re-derived at render
      // from the identified gush/חלקה (see displaySources).
      kind: 'daf_meyda',
      databaseId: 'db_rights_sheet_tlv',
      source: 'דף זכויות (מידע מוקדם — עיריית ת״א)',
      type: 'מקור סטטוטורי',
      status: hasParcel ? 'collected' : 'empty',
      date,
      link: hasParcel
        ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0`
        : null,
      note: hasParcel ? 'דף זכויות רשמי לנכס' : 'לא נמצא',
      datasets: [
        { id: 'ds_rights_sheet_land', name: 'פרטי קרקע', link: hasParcel ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0` : null },
        { id: 'ds_rights_sheet_landuse', name: 'ייעוד קרקע', link: hasParcel ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0` : null },
        { id: 'ds_rights_sheet_rights', name: 'פירוט זכויות', link: hasParcel ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0` : null },
        { id: 'ds_rights_sheet_alerts', name: 'התראות ומגבלות', link: hasParcel ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0` : null },
        { id: 'ds_rights_sheet_plans', name: 'תכניות בתוקף', link: hasParcel ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${parcel.gush}&helka=${parcel.helka}&opt=2&status=0&street=0&house=0` : null },
      ],
    },
    // NB: permits are NOT a separate source here — they live inside the building
    // file (תיק בניין → היתרים). A standalone "structured permits DB" row read as
    // "no permits found" while the archive had 20, which was contradictory.
    {
      // The actual building file (handasa archive). Starts as "scanning" and is
      // updated by the async scrape (runBuildingFileScrape) with the doc count.
      kind: 'building_file',
      databaseId: 'db_tik_binyan',
      source: 'תיק בניין (ארכיון הנדסי)',
      type: 'מסמך סטטוטורי',
      status: hasParcel ? 'scanning' : 'empty',
      date,
      link: hasParcel
        ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${parcel.gush}_${parcel.helka}`
        : null,
      note: hasParcel ? 'סורק…' : 'לא נמצא',
      datasets: [
        { id: 'ds_tik_binyan_permits', name: 'היתרים היסטוריים', link: hasParcel ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${parcel.gush}_${parcel.helka}` : null },
        { id: 'ds_tik_binyan_tashrit', name: 'תשריטי היתר / גרמושקה', link: hasParcel ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${parcel.gush}_${parcel.helka}` : null },
      ],
    },
    // Expected manual databases (spec §10) — not queryable automatically; the
    // user uploads them via the completions box. A stage-3 "מקור חסר" points here.
    {
      kind: 'manual_expected',
      gapKey: 'tabu',
      databaseId: 'db_tabu',
      source: 'טאבו / נסח מקרקעין',
      type: 'מסמך משתמש',
      status: 'pending',
      date: null,
      link: null,
      note: 'העלאה ידנית — ראו «חוסרים והשלמות»',
      datasets: [
        { id: 'ds_tabu_nesach', name: 'נסח טאבו' },
        { id: 'ds_tabu_shared', name: 'צו/תשריט בית משותף' },
      ],
    },
    {
      kind: 'manual_expected',
      gapKey: 'survey',
      databaseId: 'db_survey',
      source: 'מדידה / תכנית מצב קיים',
      type: 'מסמך משתמש',
      status: 'pending',
      date: null,
      link: null,
      note: 'העלאה ידנית — ראו «חוסרים והשלמות»',
      datasets: [
        { id: 'ds_survey_topo', name: 'מדידה טופוגרפית' },
        { id: 'ds_survey_existing', name: 'מדידת מצב קיים' },
      ],
    },
    {
      kind: 'manual_expected',
      gapKey: 'photos',
      databaseId: 'db_visual',
      source: 'תיעוד חזותי / Street View',
      type: 'מקור עזר',
      status: 'pending',
      date: null,
      link: null,
      note: 'העלאה ידנית — מקור עזר בלבד, לא לחישוב',
      datasets: [
        { id: 'ds_visual_facade', name: 'תמונות חזית' },
        { id: 'ds_visual_yard', name: 'תמונות גג / חצר' },
      ],
    },
  ];
  return rows.map((r, i) => ({ id: `src-${i + 1}`, number: i + 1, collectionMethod: r.kind === 'manual_expected' ? 'manual' : 'automatic', ...r }));
}

// Build a SourceRecord for a file the user uploads against a gap (spec §6.3):
// it takes the given number, is labelled by the GAP it completes (so the row is
// self-explanatory), keeps the file name, and remembers the gap so deleting it
// can restore the gap to the list.
export function manualSource(file, number, gap) {
  return {
    id: `src-manual-${gap?.key || number}-${file.name}`,
    number,
    source: gap?.name || file.name,
    type: 'מסמך משתמש',
    status: 'collected',
    date: new Date().toISOString(),
    link: null,
    collectionMethod: 'manual',
    manual: true,
    fileName: file.name,
    note: `${file.name} · הועלה ידנית`,
    gap: gap ? { id: gap.id, key: gap.key, name: gap.name, futureUse: gap.futureUse, importance: gap.importance } : null,
  };
}

// Shape the §8 PropertyIdentification record.
function buildIdentification(prop, parcel) {
  // Fall back to the gush/חלקה reverse-resolved from the address point.
  const gush = String(prop.block || '').trim() || parcel?.gush || '';
  const helka = String(prop.parcel || '').trim() || parcel?.helka || '';
  const address = addressText(prop);
  const hasParcel = Boolean(gush && helka);
  const status = parcel ? 'identified' : (address || hasParcel ? 'partial' : 'unidentified');
  const confidence = parcel && hasParcel ? 'high' : parcel ? 'medium' : 'low';
  return {
    address: address || null,
    additionalAddress: null,
    gush: gush || null,
    helka: helka || null,
    subParcel: String(prop.sub_parcel || '').trim() || null,
    coordinates: parcel ? { x: Math.round(parcel.coords.x), y: Math.round(parcel.coords.y) } : null,
    mapSource: parcel ? parcel.source : 'GovMap',
    status,
    confidence,
  };
}

// HH:MM:SS stamp for an agent-log event.
function logTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

// Run the full property-identification pass for a loaded project and return the
// complete view model — including `log`, a chronological record of what the
// agent actually did (the building-file scrape appends to it later).
export async function identifyProperty(loaded) {
  const prop = loaded?.property || {};
  const log = [];
  let seq = 0;
  const evt = (text, kind) => log.push({ id: `evt-${seq++}`, time: logTime(), text, kind });

  evt('התחלת איסוף נתונים על הנכס', 'info');

  // Phase 1 — locate the parcel.
  const parcel = await resolveParcel(prop);
  const identification = buildIdentification(prop, parcel);
  if (parcel) {
    evt(`הנכס זוהה — גוש ${identification.gush || '—'} / חלקה ${identification.helka || '—'} · ${parcel.source}`, 'done');
  } else {
    evt('לא אותרו קואורדינטות לנכס — דרושה כתובת או גוש/חלקה', 'error');
  }

  // Phase 2 — pull plans / permits / building file for the parcel (one call).
  let autofill = null;
  if (parcel) {
    try {
      const res = await fetch(`${BASE}/api/ask?action=rights-autofill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          x_itm: parcel.coords.x,
          y_itm: parcel.coords.y,
          gush: identification.gush,
          helka: identification.helka,
          address: identification.address,
        }),
      });
      if (res.ok) autofill = await res.json();
    } catch { /* leave autofill null — boxes render their empty state */ }
    const plans = autofill?.plans || [];
    // Permits aren't logged separately — they're part of the building file scrape
    // (logged as "תיק בניין — N מסמכים"). A separate "no permits" line contradicted
    // the archive's permit count.
    evt(plans.length ? `נמצאו ${plans.length} תכניות חלות על הנכס` : 'לא נמצאו תכניות חלות', plans.length ? 'done' : 'info');
    evt(autofill?.parcelFacts ? 'חולצו נתוני נכס מהמקורות' : 'לא נמצאו נתוני נכס נוספים', autofill?.parcelFacts ? 'done' : 'info');
  }

  const plans = (autofill?.plans || []).map(mapPlan);
  // Surface in-force plans first; rejected/cancelled ones sink to the bottom so
  // the list doesn't lead with a plan that doesn't apply. Stable within a tier.
  const TONE_RANK = { ok: 0, warn: 1, neutral: 2, bad: 3 };
  plans.sort((a, b) => (TONE_RANK[a.status.tone] ?? 9) - (TONE_RANK[b.status.tone] ?? 9));

  return {
    signature: propertySignature(prop), // for cache-reuse on revisit
    identification,
    parcel, // { coords, geometry, source } | null — for the map
    sources: deriveSources(parcel, autofill, identification.address),
    missing: deriveMissing(),
    plans,
    buildingFile: { scraped: false, categories: emptyBuildingFileCategories(), docCount: 0 },
    // parcelFacts (OCR-extracted areas/coverage/units) is intentionally NOT
    // surfaced on this screen — extraction is a later analysis stage, not
    // identification. It's kept in the snapshot for that later stage to consume.
    parcelFacts: autofill?.parcelFacts || null,
    // Same rationale as parcelFacts: the as-built permit record (floors, units,
    // built area, height) isn't identification, but chunk 04's building-permits
    // route needs it — so it rides along in the snapshot rather than being
    // re-fetched there. Snapshots saved before this field existed simply carry
    // null, and that route degrades to the OCR'd building-file facts.
    builtAs: autofill?.builtAs || null,
    log,
  };
}

// Append a building-file event to an existing log (used after the async scrape).
export function buildingFileLogEvent(text, kind) {
  return { id: `evt-bf-${Date.now()}`, time: logTime(), text, kind };
}
